<!-- src/views/TrainingView.vue -->
<template>
  <!--
    Shared centred layout for BOTH training views.
    The original venue component provides its own card.
  -->
  <div class="training-view core-page core-page--wide core-page--spaced-top">
    <div v-if="checkingAccess" class="core-panel" role="status">
      <div class="progress">
        <div class="indeterminate"></div>
      </div>
      Checking training access...
    </div>

    <div
      v-else-if="accessError"
      class="card-panel red lighten-5 red-text text-darken-3"
      role="alert"
    >
      {{ accessError }}
    </div>

    <!-- =====================================================
         OPERATIONS - GROUP TRAINING COMPLIANCE
         ===================================================== -->
    <div
      v-else-if="isOperations"
      class="card core-card core-card--bordered core-card--shadow"
    >
      <div class="card-content">
        <div class="core-section-header">
          <div class="core-title-row">
            <i class="material-icons" aria-hidden="true"> school </i>
            <div>
              <h5 class="core-card-title">Group Training Compliance</h5>
              <p class="core-card-subtitle">
                Operations overview across all venues and active team members.
              </p>
            </div>
          </div>

          <div class="core-inline">
            <span v-if="lastUpdated" class="core-helper">
              Updated {{ formatDateTime(lastUpdated) }}
            </span>

            <button
              type="button"
              class="btn-flat waves-effect"
              :disabled="groupLoading"
              @click="loadAllVenues"
            >
              <i class="material-icons left">refresh</i>
              Refresh
            </button>

            <button
              type="button"
              class="btn-small waves-effect waves-light teal"
              :disabled="groupLoading || !loadedVenues.length"
              @click="copyBriefing"
            >
              <i class="material-icons left">content_copy</i>
              Copy briefing
            </button>
          </div>
        </div>

        <div class="core-divider"></div>

        <div v-if="groupLoading" class="core-panel">
          <div class="progress">
            <div class="indeterminate"></div>
          </div>
          Loading group training information...
        </div>

        <div
          v-else-if="groupError"
          class="card-panel red lighten-5 red-text text-darken-3"
          role="alert"
        >
          {{ groupError }}
        </div>

        <template v-else>
          <div
            v-if="failedVenues.length"
            class="card-panel amber lighten-5 brown-text text-darken-3"
            role="alert"
          >
            <strong>Incomplete group data:</strong>
            {{ failedVenues.length }} venue(s) could not be loaded. These venues are
            excluded from the group totals.

            <ul class="browser-default">
              <li v-for="venue in failedVenues" :key="venue.id">
                {{ venue.label }} — {{ venue.error }}
              </li>
            </ul>
          </div>

          <template v-if="loadedVenues.length">
            <!-- GROUP SUMMARY -->
            <section class="core-section">
              <div class="core-stat-grid core-stat-grid--5">
                <div class="core-stat-tile core-stat-tile--info">
                  <span class="core-stat-label"> Group compliance </span>
                  <strong
                    class="core-stat-value"
                    :class="percentageClass(groupPercentage)"
                  >
                    {{ totalRequirements ? `${groupPercentage}%` : "—" }}
                  </strong>
                  <span class="core-stat-sub">
                    {{ currentCount }} / {{ totalRequirements }}
                    training records current
                  </span>
                </div>

                <div class="core-stat-tile core-stat-tile--success">
                  <span class="core-stat-label"> Current </span>
                  <strong class="core-stat-value">
                    {{ currentCount }}
                  </strong>
                  <span class="core-stat-sub"> Valid training records </span>
                </div>

                <div class="core-stat-tile core-stat-tile--warning">
                  <span class="core-stat-label"> Due </span>
                  <strong class="core-stat-value">
                    {{ dueCount }}
                  </strong>
                  <span class="core-stat-sub"> Within renewal grace period </span>
                </div>

                <div class="core-stat-tile core-stat-tile--danger">
                  <span class="core-stat-label"> Action required </span>
                  <strong class="core-stat-value">
                    {{ actionCount }}
                  </strong>
                  <span class="core-stat-sub"> Missing or overdue records </span>
                </div>

                <div class="core-stat-tile">
                  <span class="core-stat-label"> Staff requiring action </span>
                  <strong class="core-stat-value">
                    {{ actionStaffCount }}
                  </strong>
                  <span class="core-stat-sub">
                    Of {{ groupStaffCount }} active team members
                  </span>
                </div>
              </div>

              <div class="core-panel">
                <div class="core-progress-meta">
                  <span>Group training completion</span>
                  <strong> {{ groupPercentage }}% </strong>
                </div>

                <div class="core-progress core-progress--large core-progress--teal">
                  <div
                    class="core-progress-fill"
                    :style="{ width: `${groupPercentage}%` }"
                  ></div>
                </div>

                <div class="core-helper">
                  {{ loadedVenues.length }} venues loaded · {{ groupStaffCount }} active
                  staff · {{ dueStaffCount }} staff with training due
                </div>
              </div>
            </section>

            <!-- VENUE PERFORMANCE -->
            <section class="core-section">
              <div class="core-section-header">
                <div>
                  <h5 class="core-section-title">Venue performance</h5>
                  <p class="core-section-subtitle">
                    Highest action requirement first. Expand a venue to see the team.
                  </p>
                </div>
              </div>

              <div class="core-panel">
                <div class="table-wrap">
                  <table class="striped core-table core-table--compact">
                    <thead>
                      <tr>
                        <th>Venue</th>
                        <th>Staff</th>
                        <th>Compliance</th>
                        <th>Current</th>
                        <th>Due</th>
                        <th>Action</th>
                        <th>Staff to chase</th>
                        <th>Detail</th>
                      </tr>
                    </thead>

                    <tbody>
                      <template v-for="venue in rankedVenues" :key="venue.id">
                        <tr>
                          <td>
                            <strong>{{ venue.label }}</strong>
                          </td>

                          <td>{{ venue.activeStaffCount }}</td>

                          <td>
                            <strong :class="percentageClass(venue.percentage)">
                              {{ venue.total ? `${venue.percentage}%` : "—" }}
                            </strong>

                            <div class="core-progress core-progress--small">
                              <div
                                class="core-progress-fill"
                                :class="progressFillClass(venue.percentage)"
                                :style="{ width: `${venue.percentage}%` }"
                              ></div>
                            </div>
                          </td>

                          <td class="green-text text-darken-3">
                            <strong>{{ venue.current }}</strong>
                          </td>

                          <td class="orange-text text-darken-3">
                            <strong>{{ venue.due }}</strong>
                          </td>

                          <td class="red-text text-darken-3">
                            <strong>{{ venue.action }}</strong>
                          </td>

                          <td>
                            {{ venue.actionStaffCount }}
                          </td>

                          <td>
                            <button
                              type="button"
                              class="btn-flat btn-small teal-text text-darken-3"
                              :aria-expanded="expandedVenueId === venue.id"
                              @click="toggleVenue(venue.id)"
                            >
                              {{ expandedVenueId === venue.id ? "Hide" : "View" }}
                              <i class="material-icons right">
                                {{
                                  expandedVenueId === venue.id
                                    ? "expand_less"
                                    : "expand_more"
                                }}
                              </i>
                            </button>
                          </td>
                        </tr>

                        <tr v-if="expandedVenueId === venue.id">
                          <td colspan="8">
                            <div v-if="!venue.rows.length" class="core-helper">
                              No active staff recorded.
                            </div>

                            <div v-else class="table-wrap">
                              <table class="core-table core-table--compact">
                                <thead>
                                  <tr>
                                    <th>Team member</th>
                                    <th v-for="course in COURSES" :key="course.id">
                                      {{ course.shortTitle }}
                                    </th>
                                    <th>Overall</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <tr v-for="person in venue.rows" :key="person.key">
                                    <td>
                                      <strong>{{ person.name }}</strong>
                                      <div class="core-helper">
                                        {{ person.jobTitle }}
                                      </div>
                                    </td>

                                    <td
                                      v-for="item in person.courseStatuses"
                                      :key="item.course.id"
                                    >
                                      <div
                                        class="core-status-box"
                                        :class="statusClass(item.status.state)"
                                      >
                                        <div class="core-status-box-title">
                                          <i class="material-icons">
                                            {{ item.status.icon }}
                                          </i>
                                          <strong>
                                            {{ item.status.label }}
                                          </strong>
                                        </div>
                                        <div class="core-status-box-meta">
                                          {{ item.status.dateText }}
                                        </div>
                                      </div>
                                    </td>

                                    <td>
                                      <strong :class="percentageClass(person.percentage)">
                                        {{ person.percentage }}%
                                      </strong>
                                      <div class="core-helper">
                                        {{ person.currentCount }}/{{ COURSES.length }}
                                        current
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <!-- COURSE COMPLIANCE -->
            <section class="core-section">
              <div class="core-section-header">
                <div>
                  <h5 class="core-section-title">Compliance by training course</h5>
                  <p class="core-section-subtitle">
                    Identify the courses creating the greatest group-wide exposure.
                  </p>
                </div>
              </div>

              <div class="core-panel">
                <div class="table-wrap">
                  <table class="striped core-table core-table--compact">
                    <thead>
                      <tr>
                        <th>Course</th>
                        <th>Compliance</th>
                        <th>Current</th>
                        <th>Due</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-for="course in courseSummaries" :key="course.id">
                        <td>
                          <strong>{{ course.shortTitle }}</strong>
                        </td>

                        <td>
                          <strong :class="percentageClass(course.percentage)">
                            {{ course.total ? `${course.percentage}%` : "—" }}
                          </strong>
                          <span class="core-helper">
                            {{ course.current }}/{{ course.total }} current
                          </span>
                        </td>

                        <td class="green-text text-darken-3">
                          {{ course.current }}
                        </td>

                        <td class="orange-text text-darken-3">
                          {{ course.due }}
                        </td>

                        <td class="red-text text-darken-3">
                          <strong>{{ course.action }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <!-- TEAM FOLLOW-UP -->
            <section class="core-section">
              <div class="core-section-header">
                <div>
                  <h5 class="core-section-title">Team follow-up</h5>
                  <p class="core-section-subtitle">
                    Named training gaps for managers to resolve, sorted by priority.
                  </p>
                </div>
              </div>

              <div class="core-panel">
                <div class="core-grid core-grid--3">
                  <div>
                    <label for="training-venue-filter" class="core-label"> Venue </label>
                    <select
                      id="training-venue-filter"
                      v-model="venueFilter"
                      class="browser-default"
                    >
                      <option value="all">All venues</option>
                      <option
                        v-for="venue in rankedVenues"
                        :key="venue.id"
                        :value="venue.id"
                      >
                        {{ venue.label }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label for="training-status-filter" class="core-label"> Show </label>
                    <select
                      id="training-status-filter"
                      v-model="statusFilter"
                      class="browser-default"
                    >
                      <option value="action">Action required</option>
                      <option value="due">Due</option>
                      <option value="outstanding">All outstanding</option>
                      <option value="all">All staff</option>
                    </select>
                  </div>

                  <div>
                    <label for="training-staff-search" class="core-label"> Search </label>
                    <input
                      id="training-staff-search"
                      v-model.trim="searchText"
                      type="search"
                      placeholder="Name, role or venue"
                    />
                  </div>
                </div>

                <p class="core-helper">
                  Showing {{ visibleStaffRows.length }} of
                  {{ filteredStaffRows.length }} matching team members.
                </p>

                <div v-if="!filteredStaffRows.length" class="card-panel grey lighten-4">
                  No team members match the selected filters.
                </div>

                <div v-else class="table-wrap">
                  <table class="striped core-table core-table--compact">
                    <thead>
                      <tr>
                        <th>Team member</th>
                        <th>Venue</th>
                        <th>Missing / due training</th>
                        <th>Current</th>
                        <th>Due</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-for="person in visibleStaffRows" :key="person.key">
                        <td>
                          <strong>{{ person.name }}</strong>
                          <div class="core-helper">
                            {{ person.jobTitle }}
                          </div>
                        </td>

                        <td>{{ person.venueName }}</td>

                        <td>
                          <div
                            v-for="item in person.outstanding"
                            :key="item.course.id"
                            class="core-inline"
                          >
                            <strong> {{ item.course.shortTitle }}: </strong>
                            <span :class="statusTextClass(item.status.state)">
                              {{ item.status.label }}
                            </span>
                            <span class="core-helper">
                              {{ item.status.dateText }}
                            </span>
                          </div>

                          <span
                            v-if="!person.outstanding.length"
                            class="green-text text-darken-3"
                          >
                            All training current
                          </span>
                        </td>

                        <td class="green-text text-darken-3">
                          {{ person.currentCount }}
                        </td>

                        <td class="orange-text text-darken-3">
                          {{ person.dueCount }}
                        </td>

                        <td class="red-text text-darken-3">
                          <strong>{{ person.actionCount }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div
                  v-if="visibleStaffRows.length < filteredStaffRows.length"
                  class="center-align"
                >
                  <button
                    type="button"
                    class="btn-flat teal-text text-darken-3"
                    @click="visibleLimit += 30"
                  >
                    Show 30 more
                  </button>
                </div>
              </div>
            </section>

            <p v-if="copyMessage" class="core-helper" role="status">
              {{ copyMessage }}
            </p>
          </template>

          <div v-else-if="!failedVenues.length" class="core-panel">
            No venues were returned for the Operations account.
          </div>
        </template>
      </div>
    </div>

    <!-- =====================================================
         SINGLE-SITE / MULTI-SITE
         Original training progress card, now inside
         the SAME centred page container.
         ===================================================== -->
    <template v-else>
      <div v-if="!activeVenueId" class="core-panel">
        Select a venue in VenueSnapshot to see its team's training progress.
      </div>

      <div v-else-if="localLoading" class="core-panel" role="status">
        <div class="progress">
          <div class="indeterminate"></div>
        </div>
        Loading venue training information...
      </div>

      <div
        v-else-if="localError"
        class="card-panel red lighten-5 red-text text-darken-3"
        role="alert"
      >
        {{ localError }}
      </div>

      <StaffTrainingProgressSummary
        v-else
        :key="`${activeVenueId}-${localRefreshVersion}`"
        :venue-id="activeVenueId"
        :staff="localStaff"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { auth, authReady, db } from "@/firebase";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import { listStaffDetails } from "@/scripts/sqlApiHelper/staffDetailsApi";
import { listVenuesForSnapshotDropdown } from "@/scripts/sqlApiHelper/venuesApi";
import StaffTrainingProgressSummary from "@/components/training/StaffTrainingProgressSummary.vue";

/* =========================================================
   TRAINING CONFIGURATION
   ========================================================= */

const COURSES = [
  {
    id: "allergen_awareness_uk_2026",
    shortTitle: "Allergens",
    renewalMonths: 4,
    graceDays: 7,
  },
  {
    id: "licensing_training",
    shortTitle: "Licensing",
    renewalMonths: 6,
    graceDays: 7,
  },
  {
    id: "fruit_machine_training",
    shortTitle: "Fruit Machines",
    renewalMonths: 6,
    graceDays: 7,
  },
];

/* =========================================================
   VENUE CONTEXT AND ACCESS
   ========================================================= */

const { venueId } = useVenueContext();

const activeVenueId = computed(() => String(venueId.value || "").trim());

const checkingAccess = ref(true);
const accessError = ref("");
const isOperations = ref(false);

/* SINGLE / MULTI-SITE STATE */
const localLoading = ref(false);
const localError = ref("");
const localStaff = ref([]);
const localRefreshVersion = ref(0);

/* OPERATIONS STATE */
const reports = ref([]);
const groupLoading = ref(false);
const groupError = ref("");
const lastUpdated = ref(null);
const expandedVenueId = ref("");
const venueFilter = ref("all");
const statusFilter = ref("action");
const searchText = ref("");
const visibleLimit = ref(30);
const copyMessage = ref("");

let localRequest = 0;
let groupRequest = 0;
let disposed = false;

function clean(value) {
  return String(value ?? "").trim();
}

function toDate(value) {
  if (!value) return null;

  let date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value === "object" && typeof value.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function addMonths(value, months) {
  const date = toDate(value);
  if (!date) return null;

  const result = new Date(date);
  const originalDay = result.getDate();

  result.setMonth(result.getMonth() + months);

  if (result.getDate() !== originalDay) {
    result.setDate(0);
  }

  return result;
}

function addDays(value, days) {
  const date = toDate(value);
  if (!date) return null;

  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
}

function formatDate(value) {
  const date = toDate(value);

  return date
    ? date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "No date recorded";
}

function formatDateTime(value) {
  const date = toDate(value);

  return date
    ? date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
}

/* =========================================================
   ROLE DETECTION
   ========================================================= */

function isOperationsUser(profile = {}, claims = {}) {
  return (
    claims.operations === true ||
    claims.ops === true ||
    claims.is_operations === true ||
    profile.isOperations === true ||
    profile.is_operations === true ||
    profile.operations === true ||
    clean(profile.user_type || profile.userType).toLowerCase() === "operations"
  );
}

function isSocialMediaUser(profile = {}, claims = {}) {
  return (
    claims.social_media === true ||
    claims.socialMedia === true ||
    claims.is_social_media === true ||
    profile.isSocialMedia === true ||
    profile.is_social_media === true ||
    profile.social_media === true ||
    clean(profile.user_type || profile.userType).toLowerCase() === "social_media"
  );
}

async function checkAccess() {
  const user = auth.currentUser;

  if (!user) {
    accessError.value = "Sign in to view training progress.";
    return;
  }

  const token = await user.getIdTokenResult();
  const claims = token?.claims || {};

  let profile = {};

  try {
    const snapshot = await getDoc(doc(db, "users", user.uid));

    if (snapshot.exists()) {
      profile = snapshot.data() || {};
    }
  } catch (error) {
    if (!isOperationsUser({}, claims)) {
      throw error;
    }
  }

  if (isSocialMediaUser(profile, claims)) {
    accessError.value = "Training Progress is not available to Social Media users.";
    return;
  }

  isOperations.value = isOperationsUser(profile, claims);
}

/* =========================================================
   SINGLE / MULTI-SITE STAFF LOADING
   ========================================================= */

async function loadLocalStaff() {
  const request = ++localRequest;

  localStaff.value = [];
  localError.value = "";

  if (
    disposed ||
    checkingAccess.value ||
    accessError.value ||
    isOperations.value ||
    !activeVenueId.value
  ) {
    localLoading.value = false;
    return;
  }

  const selectedVenue = activeVenueId.value;

  localLoading.value = true;

  try {
    const result = await listStaffDetails(selectedVenue);

    if (disposed || request !== localRequest) {
      return;
    }

    localStaff.value = Array.isArray(result) ? result : [];

    localRefreshVersion.value += 1;
  } catch (error) {
    if (disposed || request !== localRequest) {
      return;
    }

    console.error("[TrainingView] Venue staff load failed:", error);

    localError.value = error?.message || "Unable to load venue staff.";
  } finally {
    if (!disposed && request === localRequest) {
      localLoading.value = false;
    }
  }
}

watch([activeVenueId, checkingAccess, isOperations], loadLocalStaff);

/* =========================================================
   TRAINING RECORD HELPERS
   ========================================================= */

function recordKey(record) {
  const staffId = clean(
    record.staffId || record.staff_id || record.userId || record.user_id
  );

  const courseId = clean(record.courseId || record.course_id);

  return staffId && courseId ? `${staffId}::${courseId}` : "";
}

function recordTime(record) {
  const date = toDate(
    record.lastCompletedAt ||
      record.completedAt ||
      record.completed_at ||
      record.updatedAt ||
      record.createdAt
  );

  return date ? date.getTime() : 0;
}

function buildRecordIndex(records) {
  const index = new Map();

  for (const record of records) {
    const key = recordKey(record);

    if (!key) continue;

    const previous = index.get(key);

    if (!previous || recordTime(record) >= recordTime(previous)) {
      index.set(key, record);
    }
  }

  return index;
}

function trainingStatus(record, course, now) {
  const completedAt = toDate(
    record?.lastCompletedAt || record?.completedAt || record?.completed_at
  );

  if (record?.passed !== true || !completedAt) {
    return {
      state: "missing",
      label: "Not completed",
      icon: "cancel",
      dateText: "No valid completion",
    };
  }

  const due =
    toDate(record?.nextDueAt || record?.next_due_at) ||
    addMonths(completedAt, course.renewalMonths);

  const grace =
    toDate(record?.graceEndsAt || record?.grace_ends_at) ||
    addDays(due, course.graceDays);

  if (due && now < due) {
    return {
      state: "current",
      label: "Current",
      icon: "check_circle",
      dateText: `Due ${formatDate(due)}`,
    };
  }

  if (due && grace && now >= due && now <= grace) {
    return {
      state: "due",
      label: "Due",
      icon: "schedule",
      dateText: `Complete by ${formatDate(grace)}`,
    };
  }

  return {
    state: "overdue",
    label: "Overdue",
    icon: "error",
    dateText: grace ? `Expired ${formatDate(grace)}` : "Training expired",
  };
}

/* =========================================================
   VENUE REPORT
   ========================================================= */

function makeVenueReport(venue, staff, records, now) {
  const index = buildRecordIndex(records);

  const rows = staff
    .filter((member) => member && member.active !== false)
    .map((member, position) => {
      const staffId = clean(member.id || member.staff_id || member.staffId);

      const courseStatuses = COURSES.map((course) => ({
        course,
        status: trainingStatus(index.get(`${staffId}::${course.id}`), course, now),
      }));

      const currentCount = courseStatuses.filter(
        (item) => item.status.state === "current"
      ).length;

      const dueCount = courseStatuses.filter((item) => item.status.state === "due")
        .length;

      const actionCount = courseStatuses.filter((item) =>
        ["missing", "overdue"].includes(item.status.state)
      ).length;

      return {
        key: `${venue.id}:${staffId || position}`,
        name: clean(member.staff_name) || "Unnamed team member",
        jobTitle: clean(member.job_title) || "No job title",
        venueId: venue.id,
        venueName: venue.label,
        courseStatuses,
        currentCount,
        dueCount,
        actionCount,
        percentage: Math.round((currentCount / COURSES.length) * 100),
        outstanding: courseStatuses.filter((item) => item.status.state !== "current"),
      };
    });

  rows.sort((a, b) => a.name.localeCompare(b.name, "en"));

  const current = rows.reduce((sum, row) => sum + row.currentCount, 0);

  const due = rows.reduce((sum, row) => sum + row.dueCount, 0);

  const action = rows.reduce((sum, row) => sum + row.actionCount, 0);

  const total = rows.length * COURSES.length;

  return {
    ...venue,
    status: "loaded",
    rows,
    activeStaffCount: rows.length,
    actionStaffCount: rows.filter((row) => row.actionCount > 0).length,
    current,
    due,
    action,
    total,
    percentage: total ? Math.round((current / total) * 100) : 0,
  };
}

/* =========================================================
   OPERATIONS - LOAD ALL VENUES
   ========================================================= */

async function loadAllVenues() {
  if (!isOperations.value || disposed) {
    return;
  }

  const request = ++groupRequest;

  groupLoading.value = true;
  groupError.value = "";
  copyMessage.value = "";

  try {
    const list = await listVenuesForSnapshotDropdown({
      isOperations: true,
      userType: "operations",
    });

    if (disposed || request !== groupRequest) {
      return;
    }

    const venues = (Array.isArray(list) ? list : [])
      .map((item) => ({
        id: clean(item.id),
        label: clean(item.label) || clean(item.id),
      }))
      .filter((item) => item.id);

    const fetched = new Array(venues.length);
    const now = new Date();
    let cursor = 0;

    async function worker() {
      while (cursor < venues.length && !disposed && request === groupRequest) {
        const position = cursor++;
        const venue = venues[position];

        try {
          const [staff, snapshot] = await Promise.all([
            listStaffDetails(venue.id),
            getDocs(collection(db, "venues", venue.id, "training_records")),
          ]);

          const records = snapshot.docs.map((document) => ({
            id: document.id,
            ...(document.data() || {}),
          }));

          fetched[position] = makeVenueReport(
            venue,
            Array.isArray(staff) ? staff : [],
            records,
            now
          );
        } catch (error) {
          console.error(`[TrainingView] Failed to load ${venue.id}:`, error);

          fetched[position] = {
            ...venue,
            status: "error",
            error: error?.message || "Unable to read training data",
          };
        }
      }
    }

    await Promise.all(
      Array.from(
        {
          length: Math.min(4, venues.length),
        },
        () => worker()
      )
    );

    if (disposed || request !== groupRequest) {
      return;
    }

    reports.value = fetched.filter(Boolean);
    lastUpdated.value = new Date();

    if (
      venueFilter.value !== "all" &&
      !reports.value.some((venue) => venue.id === venueFilter.value)
    ) {
      venueFilter.value = "all";
    }

    if (
      !reports.value.some(
        (venue) => venue.id === expandedVenueId.value && venue.status === "loaded"
      )
    ) {
      expandedVenueId.value = "";
    }
  } catch (error) {
    if (disposed || request !== groupRequest) {
      return;
    }

    console.error("[TrainingView] Group training load failed:", error);

    groupError.value = error?.message || "Unable to load group training data.";

    reports.value = [];
  } finally {
    if (!disposed && request === groupRequest) {
      groupLoading.value = false;
    }
  }
}

/* =========================================================
   GROUP COMPUTED DATA
   ========================================================= */

const loadedVenues = computed(() =>
  reports.value.filter((venue) => venue.status === "loaded")
);

const failedVenues = computed(() =>
  reports.value.filter((venue) => venue.status === "error")
);

const rankedVenues = computed(() =>
  loadedVenues.value
    .slice()
    .sort(
      (a, b) =>
        b.action - a.action ||
        b.due - a.due ||
        a.percentage - b.percentage ||
        a.label.localeCompare(b.label, "en")
    )
);

const allStaffRows = computed(() => loadedVenues.value.flatMap((venue) => venue.rows));

const groupStaffCount = computed(() => allStaffRows.value.length);

const actionStaffCount = computed(
  () => allStaffRows.value.filter((row) => row.actionCount > 0).length
);

const dueStaffCount = computed(
  () => allStaffRows.value.filter((row) => row.dueCount > 0).length
);

const totalRequirements = computed(() => groupStaffCount.value * COURSES.length);

const currentCount = computed(() =>
  loadedVenues.value.reduce((sum, venue) => sum + venue.current, 0)
);

const dueCount = computed(() =>
  loadedVenues.value.reduce((sum, venue) => sum + venue.due, 0)
);

const actionCount = computed(() =>
  loadedVenues.value.reduce((sum, venue) => sum + venue.action, 0)
);

const groupPercentage = computed(() =>
  totalRequirements.value
    ? Math.round((currentCount.value / totalRequirements.value) * 100)
    : 0
);

const courseSummaries = computed(() =>
  COURSES.map((course) => {
    const statuses = allStaffRows.value.map(
      (row) => row.courseStatuses.find((item) => item.course.id === course.id)?.status
    );

    const current = statuses.filter((status) => status?.state === "current").length;

    const due = statuses.filter((status) => status?.state === "due").length;

    const action = statuses.filter((status) =>
      ["missing", "overdue"].includes(status?.state)
    ).length;

    const total = statuses.length;

    return {
      ...course,
      current,
      due,
      action,
      total,
      percentage: total ? Math.round((current / total) * 100) : 0,
    };
  })
);

/* =========================================================
   TEAM FOLLOW-UP FILTERS
   ========================================================= */

const filteredStaffRows = computed(() => {
  const term = searchText.value.toLowerCase();

  return allStaffRows.value
    .filter((row) => {
      if (venueFilter.value !== "all" && row.venueId !== venueFilter.value) {
        return false;
      }

      if (statusFilter.value === "action" && !row.actionCount) {
        return false;
      }

      if (statusFilter.value === "due" && !row.dueCount) {
        return false;
      }

      if (statusFilter.value === "outstanding" && !row.actionCount && !row.dueCount) {
        return false;
      }

      if (
        term &&
        !`${row.name} ${row.jobTitle} ${row.venueName}`.toLowerCase().includes(term)
      ) {
        return false;
      }

      return true;
    })
    .sort(
      (a, b) =>
        b.actionCount - a.actionCount ||
        b.dueCount - a.dueCount ||
        a.venueName.localeCompare(b.venueName, "en") ||
        a.name.localeCompare(b.name, "en")
    );
});

const visibleStaffRows = computed(() =>
  filteredStaffRows.value.slice(0, visibleLimit.value)
);

watch([venueFilter, statusFilter, searchText], () => {
  visibleLimit.value = 30;
});

/* =========================================================
   PRESENTATION HELPERS
   ========================================================= */

function toggleVenue(id) {
  expandedVenueId.value = expandedVenueId.value === id ? "" : id;
}

function percentageClass(value) {
  return value >= 100
    ? "core-percent--success"
    : value >= 67
    ? "core-percent--warning"
    : "core-percent--danger";
}

function progressFillClass(value) {
  return value >= 100 ? "green" : value >= 67 ? "orange" : "red";
}

function statusClass(state) {
  return state === "current"
    ? "core-status-box--success"
    : state === "due"
    ? "core-status-box--warning"
    : "core-status-box--danger";
}

function statusTextClass(state) {
  return state === "current"
    ? "green-text text-darken-3"
    : state === "due"
    ? "orange-text text-darken-3"
    : "red-text text-darken-3";
}

/* =========================================================
   COPY OPERATIONS BRIEFING
   ========================================================= */

function buildBriefing() {
  const lines = [
    `COREDECK — GROUP TRAINING COMPLIANCE (${formatDateTime(lastUpdated.value)})`,
    `Venues reporting: ${loadedVenues.value.length} / ${reports.value.length}`,
    `Active staff: ${groupStaffCount.value}`,
    `Compliance: ${totalRequirements.value ? `${groupPercentage.value}%` : "N/A"}`,
    `Current: ${currentCount.value} | Due: ${dueCount.value} | Action required: ${actionCount.value}`,
    `Staff requiring action: ${actionStaffCount.value}`,
    "",
    "VENUE BREAKDOWN",
  ];

  rankedVenues.value.forEach((venue) => {
    lines.push(
      `${venue.label}: ${venue.percentage}% current | ` +
        `${venue.current} current | ${venue.due} due | ` +
        `${venue.action} action | ${venue.actionStaffCount} staff to chase`
    );
  });

  lines.push("", "COURSE BREAKDOWN");

  courseSummaries.value.forEach((course) => {
    lines.push(
      `${course.shortTitle}: ${course.current} current | ` +
        `${course.due} due | ${course.action} action`
    );
  });

  if (failedVenues.value.length) {
    lines.push(
      "",
      `DATA NOT AVAILABLE: ${failedVenues.value.map((venue) => venue.label).join(", ")}`
    );
  }

  return lines.join("\n");
}

async function copyBriefing() {
  const value = buildBriefing();

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
    } else {
      const field = document.createElement("textarea");
      field.value = value;
      field.style.position = "fixed";
      field.style.left = "-9999px";

      document.body.appendChild(field);

      try {
        field.select();

        if (!document.execCommand("copy")) {
          throw new Error("Copy not supported");
        }
      } finally {
        field.remove();
      }
    }

    copyMessage.value = "Group training briefing copied to clipboard.";
  } catch (error) {
    console.error("[TrainingView] Clipboard failed:", error);

    copyMessage.value =
      "Unable to copy automatically. Check browser clipboard permissions.";
  }
}

/* =========================================================
   INITIALISE / CLEANUP
   ========================================================= */

onMounted(async () => {
  try {
    await authReady;

    if (disposed) {
      return;
    }

    await checkAccess();
  } catch (error) {
    console.error("[TrainingView] Access check failed:", error);

    accessError.value = "Unable to verify training access.";
  } finally {
    checkingAccess.value = false;
  }

  if (!disposed && !accessError.value && isOperations.value) {
    await loadAllVenues();
  }
});

onUnmounted(() => {
  disposed = true;
  localRequest += 1;
  groupRequest += 1;
});
</script>
